import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewEncapsulation, ChangeDetectionStrategy, NgModule, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IonicModule, MenuController, NavController } from '@ionic/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Employee } from '@shared/classes/employee.class';
import { Page } from '@shared/classes/page.class';
import { CurrentUserModule } from '@shared/components/current-user/current-user.component';
import { ROUTES_LIST } from '@shared/constants';
import { IntercomService } from '@core/services';
import { InAppBrowserService } from '@core/services/system';
import { AdminPageService } from '@core/services/system/admin-page.service';
import { LogService } from '@core/services/system/logger';
import { RouteNavigationService } from '@core/services/system/route-navigation/route-navigation.service';
import { SecurityService } from '@core/services/system/security.service';
import { combineLatest, filter } from 'rxjs';
import { EmployeesFacadeStore } from '@state/employees';
import { PlatformService } from '@core/services/system/platform/platform.service';

@Component({
  selector: 'pos-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MenuComponent implements OnInit {
  public pages = ROUTES_LIST;
  private isSelectedItemMenu = false;
  private logger = this.logService.createLogger('MenuComponent');

  constructor(
    private securityService: SecurityService,
    private menuController: MenuController,
    private inAppBrowserService: InAppBrowserService,
    private routeNavigationService: RouteNavigationService,
    private navController: NavController,
    private adminPageService: AdminPageService,
    private intercomService: IntercomService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private logService: LogService,
    private employeesFacadeStore: EmployeesFacadeStore,
    private destroyRef: DestroyRef,
    private platformService: PlatformService
  ) {}

  ngOnInit() {
    this.handleEmployeeUpdateEvent();
  }

  private handleEmployeeUpdateEvent() {
    const activeEmployee$ = this.employeesFacadeStore.activeEmployee$;
    const loggedCompanyDataEvent = this.securityService.loggedCompanyData.asObservable();
    combineLatest([activeEmployee$, loggedCompanyDataEvent])
      .pipe(filter(([employee]) => !!employee), takeUntilDestroyed(this.destroyRef))
      .subscribe(([employee]) => {
        this.pages = this.getPagesList(ROUTES_LIST, employee);
        this.cdr.markForCheck();
      });
  }

  goToPage(page: Page) {
    if (this.isSelectedItemMenu) {
      return;
    }
    this.isSelectedItemMenu = true;
    this.navigateRoot(page.url)
      .then(() => this.menuController.close())
      .catch((error) => this.logger.error(error, 'goToPage:MenuController:close'))
      .finally(() => (this.isSelectedItemMenu = false));
  }

  hideSideMenu() {
    return this.menuController.close();
  }

  private async navigateRoot(url: string): Promise<boolean | void> {
    switch (url) {
      case 'Admin':
        if (this.platformService.isIOS) {
          this.adminPageService.openInExternalBrowser();
        } else {
          this.adminPageService.openSecured('app#/dashboard');
        }
        break;
      case 'Help':
        return this.intercomService.show();
      case 'Refer':
        const referUrl = this.translateService.instant('refer_friend_link');
        if (this.platformService.isIOS) {
          this.adminPageService.openInExternalBrowser(referUrl);
        } else {
          this.inAppBrowserService.openUrl(referUrl);
        }
        break;
      default:
        if (this.routeNavigationService.current !== url) {
          return this.navController.navigateRoot(url);
        }
        break;
    }
  }

  private getPagesList(pagesList: Page[], employee: Employee) {
    const company = this.securityService.getLoggedCompanyData();
    return pagesList.filter((currentPage) => {
      const { companySetting, permission } = currentPage;
      if (companySetting) {
        return !!company?.[companySetting];
      }
      if (permission) {
        return !!employee?.[permission];
      }
      return true;
    });
  }
}
@NgModule({
  imports: [CommonModule, IonicModule, TranslateModule.forChild(), CurrentUserModule],
  declarations: [MenuComponent],
  exports: [MenuComponent],
})
export class MenuComponentModule {}
